import { onReceiveProactiveMessage } from '$tablet-app/src/app/chat/functions';
import { ChatItem } from '@akin-home/chat';
import { Command } from '@akin-home/commands';
import { useCommandDispatch } from '@akin-home/commands-react';
import { useCallback, useMemo } from 'react';
import { useRobotConnectionListener } from './useRobotConnectionListener';
import { RobotChatMessage } from '../commands';

type RobotMessageListenerProps = {
  addChatItem: (item: ChatItem | ChatItem[]) => void;
};

/**
 * This hook enables the system to add a listener to all robot connections for
 * - messages for display chat messages on the screen
 * - messages for executing any arbritary command
 * This hook should be attached to some sort of global wrapper.
 *
 * @param props.addChatItem a function to add a chat item to the screen
 */
export const useRobotMessageListener = (props: RobotMessageListenerProps) => {
  const { addChatItem } = props;

  const dispatchCommands = useCommandDispatch();

  /*************************************
   * Handle Chat Message Request/Event *
   ************************************/

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onReceiveChatMessages = useCallback(
    onReceiveProactiveMessage({ addItem: addChatItem, dispatchCommands }),
    [addChatItem, dispatchCommands]
  );

  const chatMessageListener = useMemo(() => {
    const handleChatMessage = (chatMessage: RobotChatMessage) => {
      const { commands, text, options } = chatMessage;

      onReceiveChatMessages([
        {
          commands: commands ?? [],
          date: new Date().toISOString(),
          proactive_message: {
            options: options ?? [],
            type: 'text',
            value: text ?? 'Oh Aly, told me to do this for you.',
          },
        },
      ]);
    };

    return {
      robotId: '*',
      robotConnListeners: {
        display_chat_message: handleChatMessage,
      },
    };
  }, [onReceiveChatMessages]);

  useRobotConnectionListener(chatMessageListener);

  /***************************
   * Handle Generic Commands *
   ***************************/
  const genericCommandListener = useMemo(() => {
    const handleExecuteCommand = (commands: Command[]) => {
      dispatchCommands(commands);
    };

    return {
      robotId: '*',
      robotConnListeners: {
        execute_generic_commands: handleExecuteCommand,
      },
    };
  }, [dispatchCommands]);

  useRobotConnectionListener(genericCommandListener);
};
